/**
 * Rectangular Associative Array entity → `RenderableEntity` adapter
 * (PR-D + Q12.B).
 *
 * AutoCAD's ARRAYRECT entity replicates a base entity (or block of
 * entities) on a rows × cols grid (optionally repeated `levels` times
 * along Z — a 3-axis array), with row / column / level spacing and an
 * optional axis-rotation angle. Renderer-side this is materialised as
 * one `THREE.InstancedMesh` per base entity, with `rows × cols ×
 * levels` instance matrices baked at adapter/mesh-factory time.
 *
 * # Why InstancedMesh
 *
 * A 50×50 array of a single base entity = 2 500 instances. Drawing
 * each as a separate batched-layer slot would re-stream the same
 * vertex data 2 500 times. InstancedMesh ships one geometry plus a
 * per-instance translation matrix array — single draw call, 1.6 kB
 * of matrix data instead of N×geometry.
 *
 * # Adapter output
 *
 * The adapter emits `kind: "array-rect"` SceneGeometry carrying the
 * resolved base entity ids, the rows / cols / spacing / axis values,
 * the base point, and the levels / level-spacing (W1.5). The renderer
 * (`sceneMeshFactories.buildArrayInstancedMeshes`) walks
 * `baseEntityIds`, looks up each base in the entity feed, and builds
 * one InstancedMesh per base — `rows * cols * levels` instances,
 * transformed by `computeArrayInstanceTransforms` (XY) combined with
 * `computeArrayLevelOffsets` (Z).
 *
 * # Origin offset (Q1.A)
 *
 * `basePoint` is offset-world; per-cell deltas are NOT offset (they
 * are relative). This is consistent with how block insert handles
 * `insertion` (offset) vs the block child's local coords (untouched).
 *
 * # Edge cases (CLAUDE.md PR-D §8)
 *
 *   - Rows = 0 or Cols = 0 → adapter returns null (renderer skips).
 *     Logged via console.warn.
 *   - Source block name without resolved children → adapter still
 *     emits the array kind with empty `baseEntityIds`; renderer falls
 *     back to crosshair markers (mirrors the SVG fallback in
 *     `renderArray`).
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { applyOffset } from "../coordinateOrigin";
import { computeOcsBasis, ocsToWcs3d } from "../../geometry/ocsBasis";
import { readNumber, resolveArrayAxisAngleDeg } from "../../scene/renderHelpers";
import type {
  RenderableEntity,
  SceneGeometry,
} from "../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "./adapterContext";

export function arrayRectToInstanced(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity | null {
  const rows = Math.round(readNumber(entity.properties, "Rows", 1));
  const cols = Math.round(readNumber(entity.properties, "Columns", 1));
  if (rows <= 0 || cols <= 0) {
    if (typeof console !== "undefined") {
      console.warn(
        `[scene3d] Array entity ${entity.id} has zero rows or columns — skipped.`,
      );
    }
    return null;
  }
  const rowSpacing = readNumber(entity.properties, "Row spacing", 0);
  const colSpacing = readNumber(entity.properties, "Column spacing", 0);
  // S-ARRAYRECT-AXISANGLE-UNIT — see renderHelpers.ts:resolveArrayAxisAngleDeg
  // for the full radians-vs-degrees rationale. Feeds BOTH the bounds AABB
  // below and the geometry.axisAngleDeg the mesh factory consumes, so the
  // render and the bounds/selection box stay in agreement.
  const axisAngleDeg = resolveArrayAxisAngleDeg(entity.properties);
  const baseX = readNumber(entity.properties, "Base X", 0);
  const baseY = readNumber(entity.properties, "Base Y", 0);
  // S-ARRAYRECT-BASEPOINT-Z — the array's base elevation (ArrayRectEntity.
  // basePoint.z). Carried onto the geometry so every instance (including a
  // 3-axis array's level 0) renders AT the base elevation instead of the
  // world Z=0 plane (Codex P2, found in review).
  const baseZ = readNumber(entity.properties, "Base Z", 0);

  const basePoint = applyOffset({ x: baseX, y: baseY, z: baseZ });

  // W1.5 — 3-axis Z-level stack. `__array_level_count` / `__array_level_
  // spacing` are internal renderer-only channels (mirrors the existing
  // `__array_source_ids` convention) populated by buildArrayRectPreview
  // from the typed `ArrayRectEntity.levelCount` / `levelSpacing` (§9.4
  // AcDbAssocArray model, already round-tripped through dxf-io). Absent
  // for a plain 2D array (or a virtualized/compact-store entity, which
  // does not yet carry these optional fields) — defaults to a single
  // level so the grid renders exactly as before.
  const levels = Math.max(
    1,
    Math.round(readNumber(entity.properties, "__array_level_count", 1)),
  );
  const levelSpacing = readNumber(entity.properties, "__array_level_spacing", 0);

  // S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review round 6) — a
  // TILTED 3-axis array's base-plane normal, populated by
  // buildArrayRectPreview from `ArrayRectEntity.basePlaneNormal`. A
  // DIRECTION vector, never origin-offset (applyOffset is point-only).
  // Absent (undefined) for the common flat array — every consumer's
  // `computeOcsBasis(undefined)` identity fast-path then reproduces the
  // pre-existing WCS-Z-only stacking exactly.
  let basePlaneNormal: { x: number; y: number; z: number } | undefined;
  const rawNormal = entity.properties.__array_base_plane_normal;
  if (rawNormal) {
    try {
      const parsed = JSON.parse(rawNormal);
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof parsed.x === "number" &&
        typeof parsed.y === "number" &&
        typeof parsed.z === "number"
      ) {
        basePlaneNormal = { x: parsed.x, y: parsed.y, z: parsed.z };
      }
    } catch {
      /* fall through — malformed channel, treat as absent (default normal) */
    }
  }

  // Resolve source entity ids — populated by buildArrayRectPreview as
  // a JSON array.
  const baseEntityIds: string[] = [];
  const rawIds = entity.properties.__array_source_ids;
  if (rawIds) {
    try {
      const parsed = JSON.parse(rawIds);
      if (Array.isArray(parsed)) {
        for (const id of parsed) {
          if (typeof id === "string") baseEntityIds.push(id);
        }
      }
    } catch {
      /* fall through */
    }
  }

  // Compute bounds — the array's bounding box spans from basePoint to
  // basePoint + (cols * colSpacing, rows * rowSpacing) rotated by
  // axisAngle. We compute the rotated AABB conservatively.
  const cosA = Math.cos((axisAngleDeg * Math.PI) / 180);
  const sinA = Math.sin((axisAngleDeg * Math.PI) / 180);
  const dxMax = (cols - 1) * colSpacing;
  const dyMax = (rows - 1) * rowSpacing;
  const corners = [
    { dx: 0, dy: 0 },
    { dx: dxMax, dy: 0 },
    { dx: dxMax, dy: dyMax },
    { dx: 0, dy: dyMax },
  ];
  // S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review rounds 7 + 8) —
  // TWO tilted-array corrections to the AABB:
  //   (a) round 7: a TILTED array's higher LEVELS lean in X/Y (not just
  //       Z), so unioning the level-0-only AABB with EVERY level's XY
  //       offset is required — picking/window-select/visibility (every
  //       consumer of `renderable.bounds`) would otherwise miss instances
  //       the mesh factory genuinely draws;
  //   (b) round 8: the CELL corners themselves must be lifted through the
  //       SAME OCS basis the mesh factory now uses for the row/col grid
  //       (`computeArrayInstanceTransforms`), not the flat `cosA`/`sinA`
  //       WCS-XY formula — otherwise the bounds disagree with where the
  //       tilted cells actually render.
  // `basePlaneNormal` absent/undefined → the OCS identity fast-path
  // reproduces the EXACT pre-existing level-0-only, flat-XY AABB.
  const bandsBasis = computeOcsBasis(basePlaneNormal);
  const levelOffsetsForBounds = computeArrayLevelOffsetVectors(
    levels,
    levelSpacing,
    basePlaneNormal,
  );
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const lvl of levelOffsetsForBounds) {
    for (const c of corners) {
      const localX = c.dx * cosA - c.dy * sinA;
      const localY = c.dx * sinA + c.dy * cosA;
      const lifted = ocsToWcs3d(bandsBasis, localX, localY, 0);
      const x = basePoint.x + lifted.x + lvl.x;
      const y = basePoint.y + lifted.y + lvl.y;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX)) {
    minX = basePoint.x;
    minY = basePoint.y;
    maxX = basePoint.x;
    maxY = basePoint.y;
  }

  const geometry: SceneGeometry = {
    kind: "array-rect",
    baseEntityIds,
    rows,
    cols,
    rowSpacing,
    colSpacing,
    basePoint: { x: basePoint.x, y: basePoint.y, z: basePoint.z },
    axisAngleDeg,
    levels,
    levelSpacing,
    ...(basePlaneNormal ? { basePlaneNormal } : {}),
  };
  const bounds = { minX, minY, maxX, maxY };

  return buildRenderableShell(entity, ctx, geometry, bounds);
}

/**
 * Compute the per-cell (row, col) translation for the InstancedMesh's
 * matrix array. Exposed for the renderer + unit tests.
 *
 * Returns `rows * cols` matrices in row-major order: `(r=0,c=0),
 * (r=0,c=1), …, (r=rows-1,c=cols-1)`.
 *
 * Each matrix is a translation — `(rowIdx * rowSpacing, colIdx *
 * colSpacing)` rotated by `axisAngle` and added to `basePoint`. PR-G
 * extends this with grip-driven row/col delta overrides.
 *
 * S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review round 8) — `tz` is
 * the cell's Z-LIFT due to a TILTED `basePlaneNormal` (0 for the common
 * flat/undefined-normal array). Round 6/7 stacked LEVELS along a tilted
 * base-plane normal but left the row/column grid itself in flat WCS XY —
 * for a genuinely tilted array (e.g. normal `{x:1,y:0,z:0}`) that meant
 * columns ran parallel to the level-stacking direction instead of staying
 * IN the array's own base plane, producing a geometrically inconsistent
 * render (cells and levels disagreeing about which axis is "in-plane").
 * `(dx, dy)` — the axisAngle-rotated cell offset, computed exactly as
 * before, is now the LOCAL in-plane coordinate lifted through the SAME
 * `computeOcsBasis` the level-offset vectors use — `axisAngleDeg` is the
 * rotation WITHIN the base plane (AutoCAD's AxesAngle), the OCS basis maps
 * that plane onto WCS. `basePlaneNormal` absent/undefined → the OCS
 * identity fast-path reproduces `tz: 0` and the EXACT pre-existing
 * `tx`/`ty` formula (byte-identical for the common case).
 */
export interface ArrayInstanceTransform {
  rowIndex: number;
  colIndex: number;
  /** Translation X in offset-world space. */
  tx: number;
  /** Translation Y in offset-world space. */
  ty: number;
  /** Z-lift (relative to basePoint.z) from a tilted base-plane normal — 0
   *  for the common flat array. */
  tz: number;
}

export function computeArrayInstanceTransforms(args: {
  basePoint: { x: number; y: number };
  rows: number;
  cols: number;
  rowSpacing: number;
  colSpacing: number;
  axisAngleDeg: number;
  basePlaneNormal?: { x: number; y: number; z: number };
}): ArrayInstanceTransform[] {
  const { basePoint, rows, cols, rowSpacing, colSpacing, axisAngleDeg, basePlaneNormal } = args;
  const cosA = Math.cos((axisAngleDeg * Math.PI) / 180);
  const sinA = Math.sin((axisAngleDeg * Math.PI) / 180);
  const basis = computeOcsBasis(basePlaneNormal);
  const out: ArrayInstanceTransform[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dx = c * colSpacing;
      const dy = r * rowSpacing;
      // Rotate WITHIN the local base-plane frame first (AutoCAD's
      // AxesAngle is a plane-local rotation), THEN lift through the OCS
      // basis onto WCS. For the identity basis (flat array) this reduces
      // exactly to the pre-existing `basePoint.x/y + localX/Y` formula.
      const localX = dx * cosA - dy * sinA;
      const localY = dx * sinA + dy * cosA;
      const lifted = ocsToWcs3d(basis, localX, localY, 0);
      out.push({
        rowIndex: r,
        colIndex: c,
        tx: basePoint.x + lifted.x,
        ty: basePoint.y + lifted.y,
        tz: lifted.z,
      });
    }
  }
  return out;
}

/**
 * W1.5 — compute the per-level Z offset for a 3-axis (rows × cols ×
 * levels) rectangular array. Kept ORTHOGONAL to
 * `computeArrayInstanceTransforms` (which stays XY-only, preserving its
 * existing rows×cols contract + tests): the mesh factory combines the
 * two — each `(tx, ty)` cell is repeated once per level, translated by
 * `levelIndex * levelSpacing` along Z.
 *
 * AutoCAD's ARRAYRECT stacks levels ABOVE the base plane: level 0 sits
 * at the array's own elevation (tz = 0, relative — the base point's own
 * Z is a separate, round-trip-only concern this render arm does not
 * touch), level 1 at `+levelSpacing`, etc. Returns exactly `levels`
 * entries; `levels <= 1` returns `[0]` (single level, no stacking).
 */
export function computeArrayLevelOffsets(
  levels: number,
  levelSpacing: number,
): number[] {
  const count = Math.max(1, Math.round(levels));
  const out: number[] = [];
  for (let l = 0; l < count; l++) {
    out.push(l * levelSpacing);
  }
  return out;
}

/**
 * S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review round 6) — the FULL
 * 3-D per-level offset VECTOR for a 3-axis array, stacking along the
 * array's own base-plane normal instead of assuming WCS +Z.
 *
 * `computeArrayLevelOffsets` (above) returns pure Z scalars — correct ONLY
 * for the overwhelmingly common flat array (default WCS (0,0,1) normal,
 * where the array's local Z axis IS world Z). A REAL imported associative
 * array can be authored on a tilted base plane (`ArrayRectEntity
 * .basePlaneNormal`, already round-tripped through dxf-io); AutoCAD stacks
 * that array's levels along ITS OWN plane normal, not WCS Z — the same DXF
 * "Arbitrary Axis Algorithm" every OCS/extrusion consumer in this app uses
 * (`geometry/ocsBasis.ts`, shared with the PDF exporter's OCS lift).
 *
 * `basePlaneNormal` absent/undefined → the OCS basis's `n` axis IS WCS
 * (0,0,1) (the `computeOcsBasis` identity fast-path), so this reduces to
 * EXACTLY `{x:0, y:0, z: computeArrayLevelOffsets(...)[l]}` — byte-identical
 * to the pre-existing WCS-Z-only stacking for the common case.
 */
export function computeArrayLevelOffsetVectors(
  levels: number,
  levelSpacing: number,
  basePlaneNormal: { x: number; y: number; z: number } | undefined,
): Array<{ x: number; y: number; z: number }> {
  const scalars = computeArrayLevelOffsets(levels, levelSpacing);
  const basis = computeOcsBasis(basePlaneNormal);
  return scalars.map((s) => ({
    x: s * basis.n.x,
    y: s * basis.n.y,
    z: s * basis.n.z,
  }));
}
